import { useMemo } from 'react';
import { PortalDsBadge } from 'src/components/portal-design-system';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import {
  formatStatusLabel,
  formatSubscriptionLabel,
  getSubscriptionBadgeVariant,
} from '../utils/portal-users.mappers';
import type { PortalUser } from '../types/users.types';
import { CreditsCell } from './credits-cell';

const STATUS_VARIANTS: Record<PortalUser['status'], PortalDsBadgeVariant> = {
  active: 'green',
  blocked: 'red',
};

interface UsersTableProps {
  users: PortalUser[];
  totalCount: number;
  page: number;
  pageSize: number;
  showGuestBadge?: boolean;
  onPageChange: (page: number) => void;
  onRowClick: (user: PortalUser) => void;
}

export function UsersTable({
  users,
  totalCount,
  page,
  pageSize,
  showGuestBadge = false,
  onPageChange,
  onRowClick,
}: UsersTableProps) {
  const columns = useMemo<DataTableColumn<PortalUser>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessor: (user) => user.name,
      render: (user) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-[#0D0F1A]">
            {user.name}
          </span>
          {showGuestBadge && user.is_guest ? (
            <PortalDsBadge
              variant="amber"
              pill
            >
              Guest
            </PortalDsBadge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessor: (user) => user.email,
      render: (user) => (
        <span className="text-[#5E6278]">
          {user.email}
        </span>
      ),
    },
    {
      id: 'subscription',
      header: 'Subscription',
      accessor: (user) => user.subscription,
      render: (user) => (
        <PortalDsBadge
          variant={getSubscriptionBadgeVariant(user.subscription)}
          pill
        >
          {formatSubscriptionLabel(user.subscription)}
        </PortalDsBadge>
      ),
    },
    {
      id: 'credits',
      header: 'Credits',
      accessor: (user) => user.monthly_used,
      render: (user) => (
        <CreditsCell user={user} />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (user) => user.status,
      render: (user) => (
        <PortalDsBadge
          variant={STATUS_VARIANTS[user.status]}
          pill
        >
          {formatStatusLabel(user.status)}
        </PortalDsBadge>
      ),
    },
  ], [showGuestBadge]);

  return (
    <DataTable
      rows={users}
      columns={columns}
      pageSize={pageSize}
      getRowKey={(user) => user.id}
      emptyMessage="No users match your search or filters. Try adjusting the criteria above."
      serverPagination={{
        page,
        pageSize,
        totalCount,
        onPageChange,
      }}
      onRowClick={onRowClick}
    />
  );
}
